import React, { useState, useEffect } from 'react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart as RePieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { 
  Users, 
  FileText, 
  Newspaper, 
  TrendingUp, 
  Mail,
  Briefcase,
  Image as ImageIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { db } from '../Firebase';
import { collection, onSnapshot } from 'firebase/firestore';

const Dashboard = () => {
  // State for dynamic database counts
  const [counts, setCounts] = useState({
    loanInquiries: 0,
    contactInquiries: 0,
    jobsPosted: 0,
    jobApplications: 0,
    members: 0,
    galleryPhotos: 0,
    newsPosted: 0,
  });

  // Helper to generate rolling last 6 months (e.g., shifts automatically as months pass)
  const getRollingSixMonths = () => {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    
    for (let i = 5; i >= 0; i--) {
      // JavaScript's Date constructor handles negative months by rolling back years automatically
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        month: monthNames[d.getMonth()],
        year: d.getFullYear(),
        count: 0
      });
    }
    return months;
  };

  // State for charts
  const [loanTrendsData, setLoanTrendsData] = useState([]);
  const [categoryTrendsData, setCategoryTrendsData] = useState([]);

  // Fetch counts and aggregates from database in real-time
  useEffect(() => {
    // 1. Loan Inquiries listener (Updates Stat Card, Monthly Trends, and Category Distribution)
    const unsubLoan = onSnapshot(collection(db, 'loan_inquiries'), (snapshot) => {
      const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Update stat card count
      setCounts(prev => ({ ...prev, loanInquiries: snapshot.size }));

      // Aggregate Monthly Trends (Rolling 6 Months)
      const rollingMonths = getRollingSixMonths();
      docs.forEach(doc => {
        const date = doc.createdAt?.toDate ? doc.createdAt.toDate() : (doc.createdAt ? new Date(doc.createdAt.seconds * 1000 || doc.createdAt) : null);
        if (date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const key = `${year}-${month}`;
          
          const match = rollingMonths.find(m => m.key === key);
          if (match) {
            match.count += 1;
          }
        }
      });
      setLoanTrendsData(rollingMonths.map(m => ({ month: m.month, count: m.count })));

      // Aggregate Category Distribution
      const categoryCounts = {
        'Gold Loan': 0,
        'Mortgage Loan': 0,
        'Loan Against Deposit': 0,
        'Business Loan': 0,
        'Agriculture Allied Loan': 0,
        'Vehicle Loan': 0
      };
      
      docs.forEach(doc => {
        const category = doc.category;
        if (category && categoryCounts[category] !== undefined) {
          categoryCounts[category] += 1;
        }
      });

      const newCategoryData = [
        { name: 'Gold', value: categoryCounts['Gold Loan'] },
        { name: 'Mortgage', value: categoryCounts['Mortgage Loan'] },
        { name: 'Deposit', value: categoryCounts['Loan Against Deposit'] },
        { name: 'Business', value: categoryCounts['Business Loan'] },
        { name: 'Agriculture Allied', value: categoryCounts['Agriculture Allied Loan'] },
        { name: 'Vehicle', value: categoryCounts['Vehicle Loan'] }
      ];
      setCategoryTrendsData(newCategoryData);

    }, (error) => console.error("Error fetching loan inquiries:", error));

    // 2. Contact Inquiries Count
    const unsubContact = onSnapshot(collection(db, 'contact_inquiries'), (snapshot) => {
      setCounts(prev => ({ ...prev, contactInquiries: snapshot.size }));
    }, (error) => console.error("Error fetching contact inquiries:", error));

    // 3. Jobs Posted Count
    const unsubJobs = onSnapshot(collection(db, 'jobs'), (snapshot) => {
      setCounts(prev => ({ ...prev, jobsPosted: snapshot.size }));
    }, (error) => console.error("Error fetching jobs:", error));

    // 4. Job Applications Count
    const unsubApplications = onSnapshot(collection(db, 'job_applications'), (snapshot) => {
      setCounts(prev => ({ ...prev, jobApplications: snapshot.size }));
    }, (error) => console.error("Error fetching job applications:", error));

    // 5. Members Count
    const unsubMembers = onSnapshot(collection(db, 'members'), (snapshot) => {
      setCounts(prev => ({ ...prev, members: snapshot.size }));
    }, (error) => console.error("Error fetching members:", error));

    // 6. Gallery Photos Count
    const unsubGallery = onSnapshot(collection(db, 'gallery'), (snapshot) => {
      setCounts(prev => ({ ...prev, galleryPhotos: snapshot.size }));
    }, (error) => console.error("Error fetching gallery:", error));

    // 7. News Posted Count
    const unsubNews = onSnapshot(collection(db, 'news'), (snapshot) => {
      setCounts(prev => ({ ...prev, newsPosted: snapshot.size }));
    }, (error) => console.error("Error fetching news:", error));

    return () => {
      unsubLoan();
      unsubContact();
      unsubJobs();
      unsubApplications();
      unsubMembers();
      unsubGallery();
      unsubNews();
    };
  }, []);

  const COLORS = ['#0D47A1', '#D4A017', '#E0F2FE', '#2E7D32', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-10">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">System Overview</h1>
          <p className="text-gray-500 font-medium">Welcome back, here's what's happening today.</p>
        </div>
      </div>

      {/* Dynamic Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Card 1: Loan Inquiries */}
        <Link to="/admin/loans" className="block cursor-pointer">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.0 }}
            className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-emerald-200 transition-all group flex flex-col h-full justify-between"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-7 h-7 text-emerald-600" />
              </div>
              <span className="text-xs font-bold px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg">Loans</span>
            </div>
            <div>
              <h3 className="text-gray-500 font-bold text-xs uppercase tracking-widest mb-1">Loan Inquiries</h3>
              <p className="text-3xl font-bold text-primary-dark">{counts.loanInquiries}</p>
            </div>
          </motion.div>
        </Link>

        {/* Card 2: Contact Inquiries */}
        <Link to="/admin/contacts" className="block cursor-pointer">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-sky-200 transition-all group flex flex-col h-full justify-between"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Mail className="w-7 h-7 text-sky-600" />
              </div>
              <span className="text-xs font-bold px-2.5 py-1 bg-sky-50 text-sky-600 rounded-lg">Support</span>
            </div>
            <div>
              <h3 className="text-gray-500 font-bold text-xs uppercase tracking-widest mb-1">Contact Inquiries</h3>
              <p className="text-3xl font-bold text-primary-dark">{counts.contactInquiries}</p>
            </div>
          </motion.div>
        </Link>

        {/* Card 3: Jobs Card (Split) */}
        <Link to="/admin/jobs" className="block cursor-pointer">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-indigo-200 transition-all group flex flex-col h-full justify-between"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Briefcase className="w-7 h-7 text-indigo-600" />
              </div>
              <span className="text-xs font-bold px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg">Careers</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 border-t border-gray-50 pt-4 mt-2">
              <div>
                <h3 className="text-gray-500 font-bold text-[10px] uppercase tracking-wider mb-1">Jobs Posted</h3>
                <p className="text-2xl font-bold text-primary-dark">{counts.jobsPosted}</p>
              </div>
              <div className="border-l border-gray-100 pl-4">
                <h3 className="text-gray-500 font-bold text-[10px] uppercase tracking-wider mb-1">Applications</h3>
                <p className="text-2xl font-bold text-primary-dark">{counts.jobApplications}</p>
              </div>
            </div>
          </motion.div>
        </Link>

        {/* Card 4: Members Count */}
        <Link to="/admin/members" className="block cursor-pointer">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-amber-200 transition-all group flex flex-col h-full justify-between"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7 text-amber-600" />
              </div>
              <span className="text-xs font-bold px-2.5 py-1 bg-amber-50 text-amber-600 rounded-lg">Society</span>
            </div>
            <div>
              <h3 className="text-gray-500 font-bold text-xs uppercase tracking-widest mb-1">Total Members</h3>
              <p className="text-3xl font-bold text-primary-dark">{counts.members.toLocaleString()}</p>
            </div>
          </motion.div>
        </Link>

        {/* Card 5: Gallery Photo Count */}
        <Link to="/admin/gallery" className="block cursor-pointer">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-purple-200 transition-all group flex flex-col h-full justify-between"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <ImageIcon className="w-7 h-7 text-purple-600" />
              </div>
              <span className="text-xs font-bold px-2.5 py-1 bg-purple-50 text-purple-600 rounded-lg">Gallery</span>
            </div>
            <div>
              <h3 className="text-gray-500 font-bold text-xs uppercase tracking-widest mb-1">Gallery Photos</h3>
              <p className="text-3xl font-bold text-primary-dark">{counts.galleryPhotos}</p>
            </div>
          </motion.div>
        </Link>

        {/* Card 6: News Posted Count */}
        <Link to="/admin/news" className="block cursor-pointer">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-rose-200 transition-all group flex flex-col h-full justify-between"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Newspaper className="w-7 h-7 text-rose-600" />
              </div>
              <span className="text-xs font-bold px-2.5 py-1 bg-rose-50 text-rose-600 rounded-lg">News</span>
            </div>
            <div>
              <h3 className="text-gray-500 font-bold text-xs uppercase tracking-widest mb-1">News Posted</h3>
              <p className="text-3xl font-bold text-primary-dark">{counts.newsPosted}</p>
            </div>
          </motion.div>
        </Link>
      </div>

      {/* Dynamic Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Monthly Inquiries Chart */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-bold text-primary-dark">Loan Inquiry Trends</h3>
            <div className="p-2.5 bg-gray-50 rounded-xl">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={loanTrendsData}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0D47A1" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#0D47A1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#94A3B8', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94A3B8', fontSize: 12}} dx={-10} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
                <Area type="monotone" dataKey="count" stroke="#0D47A1" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Category Distribution Chart */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100"
        >
          <h3 className="text-xl font-bold text-primary-dark mb-8">Inquiry Categories</h3>
          <div className="h-80 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={categoryTrendsData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  paddingAngle={8}
                  dataKey="value"
                >
                  {categoryTrendsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={8} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
              </RePieChart>
            </ResponsiveContainer>
            <div className="space-y-4">
              {categoryTrendsData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                  <span className="text-sm font-bold text-gray-600">{entry.name} ({entry.value})</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default Dashboard;
